import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  SEEKING_COLLABORATION_OPTIONS,
  SECURITY_CLASSIFICATIONS,
  DEVELOPMENT_STAGES,
  FUNDING_STATUS_V2,
} from '@/lib/constants';
import TechnologyAreaPicker from './TechnologyAreaPicker';

type ExhibitorProject = {
  title: string;
  description: string;
  seekingCollaboration: string[];
  isCrada: boolean;
  classification: string;
  stage: string;
  fundingStatus: string;
  dodImpact: string;
};

type NpsProject = {
  title: string;
  description: string;
  seekingCollaboration: string[];
  classification: string;
  stage: string;
  fundingStatus: string;
  collaboratingOrganizations: string;
  technologyAreas: string[];
  dodImpact: string;
};

type Props = {
  variant: 'exhibitor' | 'nps';
  project: ExhibitorProject | NpsProject;
  onChange: (project: ExhibitorProject | NpsProject) => void;
  index: number;
  errors?: Record<string, string>;
};

export default function CollaborativeProjectForm({ variant, project, onChange, index, errors = {} }: Props) {
  const prefix = `project${index}`;
  const update = (field: string, value: unknown) => {
    onChange({ ...project, [field]: value });
  };

  const toggleCollaboration = (value: string) => {
    const current = project.seekingCollaboration;
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    update('seekingCollaboration', updated);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <h3 className="font-semibold text-base">
        Project #{index + 1}{index === 0 ? ' *' : ''}
      </h3>

      {/* Title */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Project Title * <span className="text-muted-foreground font-normal">(max 50 characters)</span></label>
        <Input
          value={project.title}
          onChange={(e) => update('title', e.target.value)}
          maxLength={50}
          placeholder="Project title"
          className="h-10"
        />
        <p className="text-xs text-muted-foreground text-right">{project.title.length}/50</p>
        {errors[`${prefix}.title`] && <p className="text-xs text-destructive">{errors[`${prefix}.title`]}</p>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Brief Project Description * <span className="text-muted-foreground font-normal">(max 400 characters)</span></label>
        <Textarea
          value={project.description}
          onChange={(e) => update('description', e.target.value)}
          maxLength={400}
          rows={4}
          placeholder="Briefly describe the problem you are addressing, current project status, current partners, and goal/potential impact."
        />
        <p className="text-xs text-muted-foreground text-right">{project.description.length}/400</p>
        {errors[`${prefix}.description`] && <p className="text-xs text-destructive">{errors[`${prefix}.description`]}</p>}
      </div>

      {/* Seeking Collaboration */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Seeking collaboration for: *</label>
        {errors[`${prefix}.seekingCollaboration`] && <p className="text-xs text-destructive">{errors[`${prefix}.seekingCollaboration`]}</p>}
        <div className="grid grid-cols-2 gap-2">
          {SEEKING_COLLABORATION_OPTIONS.map(({ value, label }) => (
            <label
              key={value}
              className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                project.seekingCollaboration.includes(value)
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Checkbox
                checked={project.seekingCollaboration.includes(value)}
                onCheckedChange={() => toggleCollaboration(value)}
                className="h-4 w-4"
              />
              <span className="text-xs">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* CRADA checkbox (exhibitor only) */}
      {variant === 'exhibitor' && 'isCrada' in project && (
        <label className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer">
          <Checkbox
            checked={(project as ExhibitorProject).isCrada}
            onCheckedChange={(checked) => update('isCrada', !!checked)}
            className="h-4 w-4"
          />
          <span className="text-sm">This project is already part of a CRADA with NPS.</span>
        </label>
      )}

      {/* Classification & Stage */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Security Classification *</label>
          <Select value={project.classification} onValueChange={(v) => update('classification', v)}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select classification" />
            </SelectTrigger>
            <SelectContent>
              {SECURITY_CLASSIFICATIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors[`${prefix}.classification`] && <p className="text-xs text-destructive">{errors[`${prefix}.classification`]}</p>}
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Development Stage *</label>
          <Select value={project.stage} onValueChange={(v) => update('stage', v)}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select stage" />
            </SelectTrigger>
            <SelectContent>
              {DEVELOPMENT_STAGES.map(({ value, label }) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors[`${prefix}.stage`] && <p className="text-xs text-destructive">{errors[`${prefix}.stage`]}</p>}
        </div>
      </div>

      {/* Funding status — required for both exhibitor and NPS projects */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Project Funding Status *</label>
        <Select
          value={project.fundingStatus}
          onValueChange={(v) => update('fundingStatus', v)}
        >
          <SelectTrigger className="h-10">
            <SelectValue placeholder="Select funding status" />
          </SelectTrigger>
          <SelectContent>
            {FUNDING_STATUS_V2.map(({ value, label }) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors[`${prefix}.fundingStatus`] && <p className="text-xs text-destructive">{errors[`${prefix}.fundingStatus`]}</p>}
      </div>

      {/* Exhibitor: Potential Mission Impact (optional) */}
      {variant === 'exhibitor' && 'isCrada' in project && (
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Potential Mission Impact <span className="text-muted-foreground font-normal">(optional, max 400 characters)</span></label>
          <Textarea
            value={(project as ExhibitorProject).dodImpact}
            onChange={(e) => update('dodImpact', e.target.value)}
            maxLength={400}
            rows={3}
            placeholder="Describe the potential impact to military operations, national defense, or DOD mission areas."
          />
          <p className="text-xs text-muted-foreground text-right">{(project as ExhibitorProject).dodImpact.length}/400</p>
          {errors[`${prefix}.dodImpact`] && <p className="text-xs text-destructive">{errors[`${prefix}.dodImpact`]}</p>}
        </div>
      )}

      {/* NPS-specific fields */}
      {variant === 'nps' && 'collaboratingOrganizations' in project && (
        <>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Other Collaborating Organizations</label>
            <Input
              value={(project as NpsProject).collaboratingOrganizations}
              onChange={(e) => update('collaboratingOrganizations', e.target.value)}
              placeholder="List all NPS departments and external organizations (separate by commas)"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Mission Impact * <span className="text-muted-foreground font-normal">(max 500 characters)</span></label>
            <Textarea
              value={(project as NpsProject).dodImpact}
              onChange={(e) => update('dodImpact', e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Describe the potential impact to military operations, national defense, or DOD mission areas."
            />
            <p className="text-xs text-muted-foreground text-right">{(project as NpsProject).dodImpact.length}/500</p>
            {errors[`${prefix}.dodImpact`] && <p className="text-xs text-destructive">{errors[`${prefix}.dodImpact`]}</p>}
          </div>

          <TechnologyAreaPicker
            selected={(project as NpsProject).technologyAreas}
            onChange={(values) => update('technologyAreas', values)}
            max={5}
            label="Project Technology Areas *"
            error={errors[`${prefix}.technologyAreas`]}
          />
        </>
      )}
    </div>
  );
}
